import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from "@/components/ui/table";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Leaf, LogOut, Download, Search, Users, ShoppingBag, Clock, Sparkles, Package, FileText, Bell, Wallet, TrendingUp, CalendarDays, ReceiptText, ImageIcon, ExternalLink, FlaskConical, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import {
  adminStats, adminLeads, adminOrders, updateLeadStatus, updateOrderStatus,
  adminSalesMonthly, downloadCsv, getToken, clearToken, formatPHP, resolveImg,
} from "@/lib/api";
import { ContentEditor } from "@/components/admin/ContentEditor";
import { ProductManager } from "@/components/admin/ProductManager";
import { ScienceManager } from "@/components/admin/ScienceManager";
import { HealthManager } from "@/components/admin/HealthManager";
import { NotificationSettings } from "@/components/admin/NotificationSettings";
import { cn } from "@/lib/utils";

const STATUS_OPTIONS = ["new", "contacted", "paid", "closed", "lost"];
const STATUS_STYLES = {
  new: "bg-[hsl(198_55%_20%)] text-[hsl(198_70%_80%)]",
  contacted: "bg-[hsl(38_60%_22%)] text-[hsl(38_90%_78%)]",
  paid: "bg-[hsl(152_55%_18%)] text-[hsl(152_60%_80%)]",
  closed: "bg-[hsl(168_45%_18%)] text-[hsl(168_55%_78%)]",
  lost: "bg-[hsl(210_10%_22%)] text-[hsl(210_10%_75%)]",
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-PH", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch (e) {
    return iso;
  }
};

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <Card className="rounded-xl border-border bg-card p-5">
    <div className="flex items-center justify-between">
      <span className={cn("flex h-10 w-10 items-center justify-center rounded-lg", accent)}>
        <Icon className="h-5 w-5" />
      </span>
    </div>
    <p className="mt-4 text-3xl font-semibold tracking-tight">{value}</p>
    <p className="mt-1 text-sm text-muted-foreground">{label}</p>
  </Card>
);

// Compact peso label for chart bars (e.g. ₱9.6k, ₱1.2M)
const fmtCompact = (v) => {
  const n = Number(v) || 0;
  if (n >= 1_000_000) return `\u20b1${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `\u20b1${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}k`;
  return `\u20b1${Math.round(n)}`;
};

const MonthlyChart = ({ data, year }) => {
  const max = Math.max(1, ...data.map((d) => Number(d.gcash) || 0));
  const total = data.reduce((s, d) => s + (Number(d.gcash) || 0), 0);
  const nowMonth = new Date().getMonth(); // 0-indexed
  return (
    <Card className="rounded-xl border-border bg-card p-5" data-testid="sales-monthly-chart">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-display text-lg font-semibold tracking-tight">Kita kada Buwan</h3>
          <p className="text-sm text-muted-foreground">GCash sales breakdown para sa {year}</p>
        </div>
        <span className="rounded-full bg-[hsl(211_100%_50%)]/15 px-3 py-1 text-sm font-medium text-[hsl(211_90%_75%)]">
          {formatPHP(total)} kabuuan
        </span>
      </div>
      <div className="mt-6 flex items-end gap-1.5 sm:gap-2" style={{ height: 176 }}>
        {data.map((d, i) => {
          const val = Number(d.gcash) || 0;
          const h = Math.round((val / max) * 140);
          const isCurrent = i === nowMonth;
          return (
            <div key={d.month} className="group flex flex-1 flex-col items-center justify-end gap-1.5" data-testid={`sales-bar-${d.month}`}>
              <span className={cn(
                "text-[10px] font-medium tabular-nums transition-opacity",
                val > 0 ? "text-foreground opacity-100" : "text-muted-foreground opacity-0 group-hover:opacity-100"
              )}>
                {val > 0 ? fmtCompact(val) : "\u20b10"}
              </span>
              <div
                className={cn(
                  "w-full rounded-t-md transition-[height,background-color] duration-200",
                  val > 0
                    ? (isCurrent ? "bg-primary" : "bg-primary/60 group-hover:bg-primary")
                    : "bg-secondary group-hover:bg-secondary/70"
                )}
                style={{ height: `${Math.max(val > 0 ? 6 : 3, h)}px` }}
                title={`${d.label} ${year}: ${formatPHP(val)} (${d.orders} order${d.orders === 1 ? "" : "s"})`}
              />
              <span className={cn("text-[10px] font-medium", isCurrent ? "text-primary" : "text-muted-foreground")}>
                {d.label}
              </span>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

const SalesDashboard = ({ stats, monthly, loading, year }) => {
  if (loading) {
    return (
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <Card key={i} className="h-28 animate-pulse rounded-xl border-border bg-card" />
        ))}
      </div>
    );
  }
  const thisMonthLabel = new Date().toLocaleDateString("en-PH", { month: "long" });
  return (
    <section className="mt-8" data-testid="sales-dashboard">
      <div className="flex items-center gap-2">
        <Wallet className="h-5 w-5 text-primary" />
        <h2 className="font-display text-xl font-semibold tracking-tight">Sales Dashboard</h2>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">Kabuuang GCash sales at buwanang kita mo.</p>

      <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="rounded-xl border-border bg-card p-5" data-testid="sales-total-card">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[hsl(152_45%_20%)] text-[hsl(152_60%_78%)]">
            <Wallet className="h-5 w-5" />
          </span>
          <p className="mt-4 text-3xl font-semibold tracking-tight text-primary">{formatPHP(stats?.gcashSalesTotal ?? 0)}</p>
          <p className="mt-1 text-sm text-muted-foreground">Kabuuang GCash sales</p>
        </Card>

        <Card className="rounded-xl border-border bg-card p-5" data-testid="sales-month-card">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[hsl(211_60%_22%)] text-[hsl(211_90%_78%)]">
            <CalendarDays className="h-5 w-5" />
          </span>
          <p className="mt-4 text-3xl font-semibold tracking-tight">{formatPHP(stats?.monthSalesTotal ?? 0)}</p>
          <p className="mt-1 text-sm text-muted-foreground">Kita ngayong {thisMonthLabel}</p>
        </Card>

        <Card className="rounded-xl border-border bg-card p-5" data-testid="sales-paid-card">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[hsl(168_45%_20%)] text-[hsl(168_55%_78%)]">
            <TrendingUp className="h-5 w-5" />
          </span>
          <p className="mt-4 text-3xl font-semibold tracking-tight">{formatPHP(stats?.paidRevenue ?? 0)}</p>
          <p className="mt-1 text-sm text-muted-foreground">Confirmed (paid) revenue</p>
        </Card>

        <Card className="rounded-xl border-border bg-card p-5" data-testid="sales-count-card">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[hsl(24_60%_24%)] text-[hsl(24_90%_78%)]">
            <ReceiptText className="h-5 w-5" />
          </span>
          <p className="mt-4 text-3xl font-semibold tracking-tight">{stats?.gcashOrdersCount ?? 0}</p>
          <p className="mt-1 text-sm text-muted-foreground">GCash orders</p>
        </Card>
      </div>

      <div className="mt-4">
        <MonthlyChart data={monthly} year={year} />
      </div>
    </section>
  );
};

const StatusSelect = ({ value, onChange, testid }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={cn("h-8 w-[130px] border-0 text-xs font-medium", STATUS_STYLES[value])} data-testid={testid}>
      <SelectValue />
    </SelectTrigger>
    <SelectContent className="dark">
      {STATUS_OPTIONS.map((s) => (
        <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
      ))}
    </SelectContent>
  </Select>
);

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [leads, setLeads] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("content");
  const [leadSearch, setLeadSearch] = useState("");
  const [orderSearch, setOrderSearch] = useState("");
  const [leadStatus, setLeadStatus] = useState("all");
  const [orderStatus, setOrderStatus] = useState("all");
  const [salesMonthly, setSalesMonthly] = useState([]);
  const [proofPreview, setProofPreview] = useState(null);
  const year = new Date().getFullYear();

  const handleAuthError = useCallback((err) => {
    if (err?.response?.status === 401) {
      clearToken();
      navigate("/admin");
    }
  }, [navigate]);

  const loadAll = useCallback(async () => {
    try {
      const [s, l, o, m] = await Promise.all([adminStats(), adminLeads(), adminOrders(), adminSalesMonthly()]);
      setStats(s);
      setLeads(l);
      setOrders(o);
      setSalesMonthly(m);
    } catch (err) {
      handleAuthError(err);
    } finally {
      setLoading(false);
    }
  }, [handleAuthError]);

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin");
      return;
    }
    loadAll();
  }, [navigate, loadAll]);

  const logout = () => {
    clearToken();
    toast.success("Naka-sign out ka na");
    navigate("/admin");
  };

  const changeLead = async (id, status) => {
    const prev = leads;
    setLeads((arr) => arr.map((x) => (x.id === id ? { ...x, status } : x)));
    try {
      await updateLeadStatus(id, status);
      const s = await adminStats(); setStats(s);
      toast.success("Na-update ang lead");
    } catch (err) {
      setLeads(prev);
      toast.error("Hindi na-update");
      handleAuthError(err);
    }
  };

  const changeOrder = async (id, status) => {
    const prev = orders;
    setOrders((arr) => arr.map((x) => (x.id === id ? { ...x, status } : x)));
    try {
      await updateOrderStatus(id, status);
      const s = await adminStats(); setStats(s);
      toast.success("Na-update ang order");
    } catch (err) {
      setOrders(prev);
      toast.error("Hindi na-update");
      handleAuthError(err);
    }
  };

  const exportCsv = async (kind) => {
    try {
      await downloadCsv(kind === "leads" ? "leads" : "orders");
      toast.success("Nagsimula ang export");
    } catch (err) {
      toast.error("Hindi na-export");
      handleAuthError(err);
    }
  };

  const filteredLeads = useMemo(() => {
    const q = leadSearch.toLowerCase();
    return leads.filter((l) => {
      const matchQ = !q || [l.name, l.email, l.phone, l.city].some((v) => (v || "").toLowerCase().includes(q));
      const matchS = leadStatus === "all" || l.status === leadStatus;
      return matchQ && matchS;
    });
  }, [leads, leadSearch, leadStatus]);

  const filteredOrders = useMemo(() => {
    const q = orderSearch.toLowerCase();
    return orders.filter((o) => {
      const matchQ = !q || [o.name, o.email, o.phone, o.productName, o.city].some((v) => (v || "").toLowerCase().includes(q));
      const matchS = orderStatus === "all" || o.status === orderStatus;
      return matchQ && matchS;
    });
  }, [orders, orderSearch, orderStatus]);

  return (
    <div className="dark min-h-screen bg-background text-foreground">
      {/* Top bar */}
      <header className="sticky top-0 z-30 border-b border-border bg-background/95 backdrop-blur">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Leaf className="h-5 w-5" />
            </span>
            <span className="font-display text-lg font-semibold">Ecentra Admin</span>
          </div>
          <Button variant="outline" size="sm" onClick={logout} data-testid="admin-logout-button">
            <LogOut className="mr-2 h-4 w-4" /> Mag-sign out
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <h1 className="font-display text-3xl font-semibold tracking-tight">Dashboard</h1>
        <p className="mt-1 text-sm text-muted-foreground">I-manage ang mga application sa team mo at mga product order.</p>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard icon={Users} label="Kabuuang team leads" value={stats?.leadsTotal ?? "—"} accent="bg-[hsl(168_45%_20%)] text-[hsl(168_55%_75%)]" />
          <StatCard icon={Sparkles} label="Bagong leads" value={stats?.leadsNew ?? "—"} accent="bg-[hsl(24_60%_24%)] text-[hsl(24_90%_78%)]" />
          <StatCard icon={ShoppingBag} label="Kabuuang orders" value={stats?.ordersTotal ?? "—"} accent="bg-[hsl(198_55%_20%)] text-[hsl(198_70%_78%)]" />
          <StatCard icon={Clock} label="Nitong 7 araw" value={(stats?.leadsLast7Days ?? 0) + (stats?.ordersLast7Days ?? 0)} accent="bg-[hsl(152_45%_20%)] text-[hsl(152_55%_78%)]" />
        </div>

        {/* Sales Dashboard */}
        <SalesDashboard stats={stats} monthly={salesMonthly} loading={loading} year={year} />

        {/* Tables */}
        <div className="mt-8">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 bg-secondary">
              <TabsTrigger value="content" data-testid="admin-tab-content"><FileText className="mr-1.5 h-4 w-4" /> Nilalaman</TabsTrigger>
              <TabsTrigger value="products" data-testid="admin-tab-products"><Package className="mr-1.5 h-4 w-4" /> Produkto</TabsTrigger>
              <TabsTrigger value="science" data-testid="admin-tab-science"><FlaskConical className="mr-1.5 h-4 w-4" /> Science</TabsTrigger>
              <TabsTrigger value="health" data-testid="admin-tab-health"><ShieldCheck className="mr-1.5 h-4 w-4" /> Health</TabsTrigger>
              <TabsTrigger value="leads" data-testid="admin-tab-leads">Team Leads ({leads.length})</TabsTrigger>
              <TabsTrigger value="orders" data-testid="admin-tab-orders">Orders ({orders.length})</TabsTrigger>
              <TabsTrigger value="settings" data-testid="admin-tab-settings"><Bell className="mr-1.5 h-4 w-4" /> Mga Setting</TabsTrigger>
            </TabsList>

            {/* CONTENT EDITOR */}
            <TabsContent value="content" className="mt-4">
              <ContentEditor />
            </TabsContent>

            {/* PRODUCT MANAGER */}
            <TabsContent value="products" className="mt-4">
              <ProductManager onChanged={loadAll} />
            </TabsContent>

            <TabsContent value="science" className="mt-4">
              <ScienceManager />
            </TabsContent>

            <TabsContent value="health" className="mt-4">
              <HealthManager />
            </TabsContent>

            {/* NOTIFICATION SETTINGS */}
            <TabsContent value="settings" className="mt-4">
              <NotificationSettings />
            </TabsContent>

            {/* LEADS */}
            <TabsContent value="leads" className="mt-4">
              <Card className="rounded-xl border-border bg-card">
                <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
                    <div className="relative sm:max-w-xs sm:flex-1">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input className="h-10 pl-9" placeholder="Hanapin: pangalan, email, numero..." value={leadSearch} onChange={(e) => setLeadSearch(e.target.value)} data-testid="admin-leads-search-input" />
                    </div>
                    <Select value={leadStatus} onValueChange={setLeadStatus}>
                      <SelectTrigger className="h-10 w-[150px]" data-testid="admin-leads-status-filter"><SelectValue /></SelectTrigger>
                      <SelectContent className="dark">
                        <SelectItem value="all">All statuses</SelectItem>
                        {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="outline" onClick={() => exportCsv("leads")} data-testid="admin-leads-export-csv-button">
                    <Download className="mr-2 h-4 w-4" /> Export CSV
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Petsa</TableHead>
                        <TableHead>Pangalan</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Lungsod</TableHead>
                        <TableHead className="min-w-[200px]">Mensahe</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {!loading && filteredLeads.map((l) => (
                        <TableRow key={l.id} data-testid="admin-lead-row">
                          <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmtDate(l.createdAt)}</TableCell>
                          <TableCell className="font-medium">{l.name}</TableCell>
                          <TableCell className="text-sm"><div>{l.email}</div><div className="text-muted-foreground">{l.phone}</div></TableCell>
                          <TableCell className="text-sm text-muted-foreground">{l.city || "—"}</TableCell>
                          <TableCell className="max-w-[260px] text-sm text-muted-foreground"><span className="line-clamp-2">{l.message || "—"}</span></TableCell>
                          <TableCell><StatusSelect value={l.status} onChange={(v) => changeLead(l.id, v)} testid="admin-lead-status-select" /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {!loading && filteredLeads.length === 0 && (
                    <div className="p-10 text-center text-sm text-muted-foreground">Wala pang team leads. I-share ang landing link mo para magsimula ng mga application.</div>
                  )}
                  {loading && <div className="p-10 text-center text-sm text-muted-foreground">Naglo-load...</div>}
                </div>
              </Card>
            </TabsContent>

            {/* ORDERS */}
            <TabsContent value="orders" className="mt-4">
              <Card className="rounded-xl border-border bg-card">
                <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
                    <div className="relative sm:max-w-xs sm:flex-1">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                      <Input className="h-10 pl-9" placeholder="Hanapin: produkto, pangalan, email..." value={orderSearch} onChange={(e) => setOrderSearch(e.target.value)} data-testid="admin-orders-search-input" />
                    </div>
                    <Select value={orderStatus} onValueChange={setOrderStatus}>
                      <SelectTrigger className="h-10 w-[150px]" data-testid="admin-orders-status-filter"><SelectValue /></SelectTrigger>
                      <SelectContent className="dark">
                        <SelectItem value="all">All statuses</SelectItem>
                        {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button variant="outline" onClick={() => exportCsv("orders")} data-testid="admin-orders-export-csv-button">
                    <Download className="mr-2 h-4 w-4" /> Export CSV
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Petsa</TableHead>
                        <TableHead>Produkto</TableHead>
                        <TableHead>Dami</TableHead>
                        <TableHead>Bayad</TableHead>
                        <TableHead>Proof</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {!loading && filteredOrders.map((o) => (
                        <TableRow key={o.id} data-testid="admin-order-row">
                          <TableCell className="whitespace-nowrap text-xs text-muted-foreground">{fmtDate(o.createdAt)}</TableCell>
                          <TableCell className="font-medium">
                            {o.productName || "General inquiry"}
                            {o.variantText ? <div className="text-xs font-normal text-muted-foreground">{o.variantText}</div> : null}
                          </TableCell>
                          <TableCell>{o.quantity}</TableCell>
                          <TableCell className="text-sm">
                            {o.paymentMethod === "gcash" ? (
                              <div>
                                <span className="inline-flex items-center rounded-full bg-[hsl(211_100%_50%)]/15 px-2 py-0.5 text-xs font-medium text-[hsl(211_90%_75%)]">GCash</span>
                                <div className="mt-1 font-medium text-foreground">{o.amount ? formatPHP(o.amount) : "—"}</div>
                                {o.gcashReference ? <div className="text-xs text-muted-foreground">Ref: {o.gcashReference}</div> : null}
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">Inquiry</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {o.proofImage ? (
                              <button
                                type="button"
                                onClick={() => setProofPreview({ url: o.proofImage, order: o })}
                                className="group relative block h-11 w-11 overflow-hidden rounded-md border border-border transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                title="Tingnan ang GCash proof"
                                data-testid="order-proof-thumb"
                              >
                                <img src={resolveImg(o.proofImage)} alt="GCash proof" className="h-full w-full object-cover" />
                                <span className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                                  <Search className="h-4 w-4 text-white" />
                                </span>
                              </button>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" data-testid="order-proof-none">
                                <ImageIcon className="h-3.5 w-3.5" /> Wala
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-sm">{o.name}<div className="text-muted-foreground">{o.city}</div></TableCell>
                          <TableCell className="text-sm"><div>{o.email}</div><div className="text-muted-foreground">{o.phone}</div></TableCell>
                          <TableCell><StatusSelect value={o.status} onChange={(v) => changeOrder(o.id, v)} testid="admin-order-status-select" /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {!loading && filteredOrders.length === 0 && (
                    <div className="p-10 text-center text-sm text-muted-foreground">Wala pang orders. I-share ang mga produkto mula sa landing page mo para makatanggap ng inquiries.</div>
                  )}
                  {loading && <div className="p-10 text-center text-sm text-muted-foreground">Naglo-load...</div>}
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      {/* GCash proof preview */}
      <Dialog open={!!proofPreview} onOpenChange={(o) => !o && setProofPreview(null)}>
        <DialogContent className="dark max-w-lg" data-testid="order-proof-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">GCash Payment Proof</DialogTitle>
          </DialogHeader>
          {proofPreview ? (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-muted-foreground">Customer</p>
                  <p className="font-medium">{proofPreview.order?.name || "—"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Halaga</p>
                  <p className="font-medium text-primary">{proofPreview.order?.amount ? formatPHP(proofPreview.order.amount) : "—"}</p>
                </div>
                {proofPreview.order?.gcashReference ? (
                  <div className="col-span-2">
                    <p className="text-muted-foreground">GCash Ref.</p>
                    <p className="font-mono font-medium">{proofPreview.order.gcashReference}</p>
                  </div>
                ) : null}
              </div>
              <div className="overflow-hidden rounded-lg border border-border bg-secondary/40">
                <img src={resolveImg(proofPreview.url)} alt="GCash payment proof" className="max-h-[60vh] w-full object-contain" data-testid="order-proof-image" />
              </div>
              <a
                href={resolveImg(proofPreview.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                data-testid="order-proof-open-new-tab"
              >
                <ExternalLink className="h-4 w-4" /> Buksan sa bagong tab
              </a>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
